import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AvailableServices } from '@vendasta/integrations';
import { MeetingSource, Preferences } from '@vendasta/meetings';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest, from, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  skip,
  switchMap,
  tap,
} from 'rxjs/operators';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { IntegrationServiceSelection } from '../../meeting-integrations/integration-selection/integration-selection.component';
import { ScheduleSettingsViewService } from '../schedule-settings-view.service';
import { isInSettingsMode } from '../../utils';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { POSTHOG_ACTIONS, POSTHOG_CATEGORIES, POSTHOG_KEYS, getMeetingAppDisplayName } from '../../constants';

const meetingSourceToAvailableService = new Map<MeetingSource, AvailableServices>();
meetingSourceToAvailableService.set(MeetingSource.MEETING_SOURCE_GOOGLE_MEET, AvailableServices.GOOGLE_MEET);
meetingSourceToAvailableService.set(MeetingSource.MEETING_SOURCE_ZOOM, AvailableServices.ZOOM);
meetingSourceToAvailableService.set(MeetingSource.MEETING_SOURCE_MICROSOFT, AvailableServices.MICROSOFT);

const availableServiceToMeetingSource = new Map<AvailableServices, MeetingSource>();
availableServiceToMeetingSource.set(AvailableServices.GOOGLE_MEET, MeetingSource.MEETING_SOURCE_GOOGLE_MEET);
availableServiceToMeetingSource.set(AvailableServices.ZOOM, MeetingSource.MEETING_SOURCE_ZOOM);
availableServiceToMeetingSource.set(AvailableServices.MICROSOFT, MeetingSource.MEETING_SOURCE_MICROSOFT);

@Component({
  selector: 'meeting-scheduler-settings-meeting-app-card',
  templateUrl: './meeting-app-card.component.html',
  styleUrls: ['./meeting-app-card.component.scss'],
  standalone: false,
})
export class MeetingAppCardComponent implements OnInit, OnDestroy {
  @Input() serviceKey: string;
  @Input() isGoogleMeetConfigured: boolean;
  @Input() isMicrosoftConfigured: boolean;
  @Input() enableTracking: boolean;

  @Input() set hostPreferences(preferences: Preferences) {
    this.hostPreferences$$.next(preferences);
  }

  @Output() integrationSelection: EventEmitter<IntegrationServiceSelection> = new EventEmitter();

  initialServiceSelection$: Observable<AvailableServices>;
  selection$: Observable<IntegrationServiceSelection>;

  readonly meet = AvailableServices.GOOGLE_MEET;
  readonly zoom = AvailableServices.ZOOM;
  readonly microsoft = AvailableServices.MICROSOFT;

  private readonly selection$$: BehaviorSubject<IntegrationServiceSelection> = new BehaviorSubject(undefined);
  private readonly subscriptions: Subscription[] = [];
  private readonly hostPreferences$$: ReplaySubject<Preferences> = new ReplaySubject(1);
  private previousSelection: IntegrationServiceSelection | null = null;

  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly scheduleSettingsViewService: ScheduleSettingsViewService,
    private readonly alertService: SnackbarService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.selection$ = this.selection$$.pipe(filter<IntegrationServiceSelection>(Boolean));

    const hostServicePreference$ = this.hostPreferences$$.pipe(
      filter<Preferences>(Boolean),
      map((preferences) => meetingSourceToAvailableService.get(preferences.meetingIntegration)),
    );
    this.initialServiceSelection$ = combineLatest([
      this.scheduleSettingsViewService.meetingAppSelection$,
      hostServicePreference$,
    ]).pipe(
      map(([selection, preference]) => {
        return selection || preference;
      }),
      filter<AvailableServices>(Boolean),
    );

    const setPreference$ = this.selection$.pipe(
      tap((s) => this.scheduleSettingsViewService.setMeetingAppSelection(s.service)),
      filter((s) => s.isConnected),
      distinctUntilChanged((s1, s2) => s1.service === s2.service && s1.isConnected === s2.isConnected),
      switchMap((selection) =>
        from(
          this.meetingSchedulerStoreService.updateUserPreferences({
            meetingIntegration: availableServiceToMeetingSource.get(selection.service),
          }),
        ),
      ),
      shareReplay(1),
    );
    this.subscriptions.push(
      setPreference$.subscribe({
        error: () =>
          this.alertService.openErrorSnack(
            'MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.SET_PREFERRED_MEETING_APP_ERROR',
          ),
      }),
    );
    // Skip the first call because we call updatePreference initially because we want there to be a preference set.
    this.subscriptions.push(
      setPreference$
        .pipe(
          skip(1),
          mapTo(true),
          catchError(() => of(false)),
        )
        .subscribe((success) => {
          if (success) {
            // Original tracking for all contexts
            if (this.enableTracking) {
              this.analyticsService.trackEvent(
                POSTHOG_KEYS.HOST_PREFERENCE_UPDATED,
                POSTHOG_CATEGORIES.USER,
                POSTHOG_ACTIONS.CLICK,
              );
            }

            // Settings-specific tracking for meeting app changes
            // Only track MEETING_APP_CHANGED events when not in setup wizard mode
            // This ensures we differentiate between initial setup and ongoing changes
            if (isInSettingsMode(this.route)) {
              const currentSelection = this.selection$$.value;
              if (currentSelection) {
                this.trackMeetingAppChange(currentSelection);
              }
            }

            this.alertService.openSuccessSnack(
              'MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.SET_PREFERRED_MEETING_APP_SUCCESS',
            );
          }
        }),
    );

    // Track OAuth completion for meeting apps
    // This subscription detects when a previously unconnected app becomes connected
    // after an OAuth flow, which is a common scenario in the settings page
    this.subscriptions.push(
      this.selection$.subscribe((selection) => {
        if (isInSettingsMode(this.route) && this.previousSelection) {
          // Check if same app became connected (OAuth completion)
          const wasNotConnected =
            this.previousSelection.service === selection.service && !this.previousSelection.isConnected;
          const isNowConnected = selection.isConnected;

          if (wasNotConnected && isNowConnected) {
            // App just became connected via OAuth - track the change
            this.trackMeetingAppChange(selection);
          }
        }
        // Always update previous selection for next comparison
        this.previousSelection = selection;
      }),
    );
  }

  private trackMeetingAppChange(selection: IntegrationServiceSelection): void {
    // Only track meeting app changes in settings mode for connected apps
    // This prevents false tracking during initialization or for unconnected apps
    // Connected apps are the only ones where user can actually use the selection
    if (this.enableTracking && selection.isConnected) {
      this.analyticsService.trackEvent(
        POSTHOG_KEYS.MEETING_APP_CHANGED,
        POSTHOG_CATEGORIES.USER,
        POSTHOG_ACTIONS.CLICK,
        undefined,
        {
          calendar_or_meeting_provider: getMeetingAppDisplayName(selection.service),
        },
      );
    }
  }

  onIntegrationSelected(selection: IntegrationServiceSelection): void {
    this.selection$$.next(selection);
    this.integrationSelection.next(selection);

    // Track immediate selection of already-connected apps in settings
    // If user switches to a different app that's already connected, track immediately
    // This handles the case where no OAuth flow is needed
    if (isInSettingsMode(this.route) && selection.isConnected) {
      const isDifferentApp = !this.previousSelection || selection.service !== this.previousSelection.service;

      if (isDifferentApp) {
        // User selected a different, already-connected app - track immediately
        this.trackMeetingAppChange(selection);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
